/**
 * Always-on global socket event handlers.
 *
 * These handlers keep conversation lists, unread counts, and message previews
 * fresh even when no specific conversation is open.
 */
import { useEffect } from 'react';
import { Socket } from 'socket.io-client';
import { reduxLogger } from '@/core/utils/logger';
import { ServerEvents, type MessageCreatedPayload, type MessageStatusUpdatePayload } from '@voley/shared-types/socket';
import { parseServerEvent, messageCreatedPayloadSchema, messageStatusUpdatePayloadSchema } from '../../socket/socket.schemas';
import { baseApi } from '@/api/core/baseApi';
import { store, type RootState } from '../../store';
import { conversationQueriesApi, ConversationData } from '@/api/features/conversations';
import { recentlyMutatedConversationIds } from '@/api/features/conversations/endpoints/mutations';
import { buildMessagePreview, getMessageTimestamp, type RawMessageInput } from '@/api/features/messaging/utils/normalize';
import { teamChatApi } from '@/api/features/teams';

// RTK Query cache iteration helpers
type ApiState = RootState[typeof baseApi.reducerPath];
interface CacheEntry { originalArgs?: unknown; data?: unknown; status?: string }

function forEachConversationCache(
  updater: (args: unknown, draft: { items: ConversationData[]; total?: number }) => void
) {
  const state = store.getState();
  const apiState: ApiState = state[baseApi.reducerPath];
  const queries = (apiState?.queries ?? {}) as Record<string, CacheEntry | undefined>;
  Object.entries(queries).forEach(([key, entry]) => {
    if (!entry) return;
    if (key.startsWith('getConversations(')) {
      const args = entry.originalArgs;
      store.dispatch(
        conversationQueriesApi.util.updateQueryData(
          'getConversations',
          args as Parameters<typeof conversationQueriesApi.endpoints.getConversations.initiate>[0],
          (draft) => { updater(args, draft as { items: ConversationData[]; total?: number }); }
        )
      );
    } else if (key.startsWith('getInfiniteConversations-')) {
      const args = entry.originalArgs;
      store.dispatch(
        conversationQueriesApi.util.updateQueryData(
          'getInfiniteConversations',
          args as Parameters<typeof conversationQueriesApi.endpoints.getInfiniteConversations.initiate>[0],
          (draft) => { updater(args, draft as { items: ConversationData[]; total?: number }); }
        )
      );
    }
  });
}

function forEachTeamConversationCache(
  updater: (args: unknown, draft: Array<{ id?: string; isRead?: boolean; unreadCount?: number; [k: string]: unknown }>) => void
) {
  const state = store.getState();
  const apiState: ApiState = state[baseApi.reducerPath];
  const queries = (apiState?.queries ?? {}) as Record<string, CacheEntry | undefined>;
  Object.entries(queries).forEach(([key, entry]) => {
    if (!key.startsWith('getTeamConversations(') || !entry) return;
    const args = entry.originalArgs;
    store.dispatch(
      teamChatApi.util.updateQueryData(
        'getTeamConversations',
        args as Parameters<typeof teamChatApi.endpoints.getTeamConversations.initiate>[0],
        (draft) => { updater(args, draft as unknown as Array<{ id?: string; isRead?: boolean; unreadCount?: number; [k: string]: unknown }>); }
      )
    );
  });
}

function toEpochMs(ts: number | string | Date | undefined | null): number {
  if (ts == null) return 0;
  if (typeof ts === 'number') return ts;
  return new Date(ts).getTime();
}

// Cache update helpers
function bumpUnreadInAllLists(convId: string, nextTimestamp?: number, ignoreIfActive = true) {
  try {
    const state = store.getState();
    const activeConversationId: string | null = state.messagingUi?.currentConversationId || null;
    if (ignoreIfActive && activeConversationId && convId === activeConversationId) return;
    forEachConversationCache((_args, draft) => {
      if (draft && draft.items) {
        const idx = draft.items.findIndex((c) => c.id === convId);
        if (idx !== -1) {
          if (nextTimestamp !== undefined) {
            const prev = draft.items[idx];
            const prevTs = toEpochMs(prev.timestamp);
            if (nextTimestamp <= prevTs) return;
          }
          draft.items[idx].unreadCount = (draft.items[idx].unreadCount || 0) + 1;
        }
      }
    });
  } catch {}
}

function updatePreviewInAllLists(convId: string, lastMessage?: string, messageTimestamp?: number) {
  try {
    forEachConversationCache((_args, draft) => {
      if (draft && draft.items) {
        const idx = draft.items.findIndex((c) => c.id === convId);
        if (idx !== -1) {
          if (lastMessage !== undefined) draft.items[idx].lastMessage = lastMessage;
          if (messageTimestamp !== undefined) draft.items[idx].timestamp = messageTimestamp;
          if (idx > 0) {
            const [updatedConversation] = draft.items.splice(idx, 1);
            draft.items.unshift(updatedConversation);
          }
        }
      }
    });
  } catch {}
}

function setUnreadInAllLists(convId: string, unreadCount: number) {
  try {
    forEachConversationCache((_args, draft) => {
      if (draft && draft.items) {
        const idx = draft.items.findIndex((c) => c.id === convId);
        if (idx !== -1) {
          draft.items[idx].unreadCount = Math.max(0, Number.isFinite(unreadCount) ? unreadCount : 0);
        }
      }
    });
  } catch {}
}

function clearUnreadInAllLists(convId: string) {
  try {
    forEachConversationCache((_args, draft) => {
      if (draft && draft.items) {
        const idx = draft.items.findIndex((c) => c.id === convId);
        if (idx !== -1) draft.items[idx].unreadCount = 0;
      }
    });
  } catch {}
}

function markTeamConversationUnread(convId: string) {
  try {
    forEachTeamConversationCache((_args, draft) => {
      const idx = draft.findIndex((c) => c.id === convId);
      if (idx !== -1) {
        draft[idx].isRead = false;
        draft[idx].unreadCount = (draft[idx].unreadCount || 0) + 1;
      }
    });
  } catch {}
}

function clearTeamConversationUnread(convId: string) {
  try {
    forEachTeamConversationCache((_args, draft) => {
      const idx = draft.findIndex((c) => c.id === convId);
      if (idx !== -1) { draft[idx].isRead = true; draft[idx].unreadCount = 0; }
    });
  } catch {}
}

function setTeamConversationUnreadCount(convId: string, unreadCount: number) {
  try {
    forEachTeamConversationCache((_args, draft) => {
      const idx = draft.findIndex((c) => c.id === convId);
      if (idx !== -1) {
        const count = Math.max(0, Number.isFinite(unreadCount) ? unreadCount : 0);
        draft[idx].isRead = count === 0;
        draft[idx].unreadCount = count;
      }
    });
  } catch {}
}

// Payload types
interface ConversationUpdatedPayload {
  id?: string;
  conversationId?: string;
  timestamp?: number | string;
  lastMessage?: string;
  unreadCount?: number;
  [k: string]: unknown;
}

interface MessagesReadPayload {
  conversationId?: string;
  readerId?: string;
  reader?: { id: string; name: string };
  [k: string]: unknown;
}

interface UnreadCountUpdatedPayload {
  conversationId?: string;
  unreadCount?: number;
  [k: string]: unknown;
}

/**
 * Registers global socket event handlers on the given socket instance.
 * Must be called inside the SocketProvider after connection is established.
 */
export function useSocketGlobalHandlers(
  socket: Socket | null,
  registerHandler: (socket: Socket, event: string, handler: (...args: unknown[]) => void) => void
) {
  useEffect(() => {
    if (!socket) return;

    // Coalesced tag invalidation
    type CacheTag = Extract<Parameters<typeof baseApi.util.invalidateTags>[0][number], { type: string }>;
    const pendingTags = new Map<string, CacheTag>();
    let invalidateTimer: ReturnType<typeof setTimeout> | null = null;

    const coalescedInvalidate = (tags: CacheTag[]) => {
      for (const tag of tags) {
        pendingTags.set(`${tag.type}:${tag.id}`, tag);
      }
      if (invalidateTimer) clearTimeout(invalidateTimer);
      invalidateTimer = setTimeout(() => {
        invalidateTimer = null;
        const batch = Array.from(pendingTags.values());
        pendingTags.clear();
        if (batch.length > 0) {
          store.dispatch(baseApi.util.invalidateTags(batch));
        }
      }, 500);
    };

    const handleMessageCreated = (rawData: unknown) => {
      reduxLogger.info('[SocketProvider] message_created (global)');
      const { data } = parseServerEvent(messageCreatedPayloadSchema, rawData, ServerEvents.MESSAGE_CREATED);
      const payload = data as MessageCreatedPayload;
      const convId = payload?.conversationId || payload?.message?.conversationId;

      const state = store.getState();
      const currentUserId: string | null = state.userProfile?.session?.id || null;
      const senderId = payload?.message?.sender?.id || payload?.message?.senderId;
      const isSelfSentMessage = currentUserId && senderId && senderId === currentUserId;

      if (!isSelfSentMessage) {
        const activeConversationId: string | null = state.messagingUi?.currentConversationId || null;
        const tags: Array<{ type: 'Messages' | 'TeamConversations'; id: string }> = [
          { type: 'Messages', id: 'LIST' },
          { type: 'TeamConversations', id: 'LIST' }
        ];
        if (convId && convId !== activeConversationId) {
          tags.push({ type: 'Messages', id: convId });
        }
        coalescedInvalidate(tags);
      }

      try {
        const activeConversationId: string | null = state.messagingUi?.currentConversationId || null;
        if (convId && (!activeConversationId || convId !== activeConversationId)) {
          if (!isSelfSentMessage) {
            bumpUnreadInAllLists(convId);
            markTeamConversationUnread(convId);
          }
        }
      } catch {}

      if (!isSelfSentMessage) {
        try {
          if (convId) {
            const msg = (payload?.message || payload) as RawMessageInput;
            const content = buildMessagePreview(msg);
            const msgTs = getMessageTimestamp(msg);
            updatePreviewInAllLists(convId, content, msgTs);
          }
        } catch {}
      }
    };

    const handleMessageStatusUpdate = (rawData: unknown) => {
      reduxLogger.info('[SocketProvider] message_status_update (global)');
      const { data } = parseServerEvent(messageStatusUpdatePayloadSchema, rawData, ServerEvents.MESSAGE_STATUS_UPDATE);
      const payload = data as MessageStatusUpdatePayload;
      const convId = payload?.conversationId;
      const state = store.getState();
      const activeConversationId = state.messagingUi?.currentConversationId || null;
      if (convId && convId !== activeConversationId) {
        coalescedInvalidate([{ type: 'Messages', id: convId }]);
      }
    };

    const handleConversationUpdated = (data: ConversationUpdatedPayload) => {
      reduxLogger.info('[SocketProvider] conversation_updated (global)');
      const convId = data?.id || data?.conversationId;
      try {
        if (!convId) return;
        const nextTs = data?.timestamp ? toEpochMs(data.timestamp) : undefined;
        const lastMessage = data?.lastMessage ? data.lastMessage : undefined;
        updatePreviewInAllLists(convId, lastMessage, nextTs);
        const explicitUnread = data?.unreadCount;
        if (typeof explicitUnread === 'number') {
          if (recentlyMutatedConversationIds.has(convId)) return;
          const currentState = store.getState();
          const activeConversationId = currentState.messagingUi?.currentConversationId || null;
          if (!activeConversationId || convId !== activeConversationId) {
            setUnreadInAllLists(convId, explicitUnread);
            setTeamConversationUnreadCount(convId, explicitUnread);
          }
        }
      } catch {}
    };

    const handleMessagesRead = (data: MessagesReadPayload) => {
      try {
        const convId = data?.conversationId;
        if (!convId) return;

        // Only clear unread for the user who actually read it.
        // reader.id comes from socket MARK_READ; readerId from REST mark-read.
        const eventReaderId = data?.reader?.id || data?.readerId;
        const state = store.getState();
        const currentUserId: string | null = state.userProfile?.session?.id || null;
        if (eventReaderId && currentUserId && eventReaderId !== currentUserId) return;

        clearUnreadInAllLists(convId);
        clearTeamConversationUnread(convId);
      } catch {}
    };

    const handleUnreadCountUpdated = (data: UnreadCountUpdatedPayload) => {
      try {
        const convId = data?.conversationId;
        if (!convId) return;
        if (recentlyMutatedConversationIds.has(convId)) return;
        const currentState = store.getState();
        const activeConversationId = currentState.messagingUi?.currentConversationId || null;
        if (activeConversationId && convId === activeConversationId) return;
        const explicitUnread = data?.unreadCount;
        if (typeof explicitUnread === 'number') {
          setUnreadInAllLists(convId, explicitUnread);
          setTeamConversationUnreadCount(convId, explicitUnread);
        }
      } catch {}
    };

    registerHandler(socket, ServerEvents.MESSAGE_CREATED, handleMessageCreated);
    registerHandler(socket, ServerEvents.MESSAGE_STATUS_UPDATE, handleMessageStatusUpdate);
    registerHandler(socket, ServerEvents.CONVERSATION_UPDATED, handleConversationUpdated);
    registerHandler(socket, ServerEvents.MESSAGES_READ, handleMessagesRead);
    registerHandler(socket, ServerEvents.UNREAD_COUNT_UPDATED, handleUnreadCountUpdated);

    return () => {
      if (invalidateTimer) clearTimeout(invalidateTimer);
    };
  }, [socket, registerHandler]);
}
